import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { api } from '../App';
import { 
  BookOpen, LogOut, Users, Building2, GraduationCap, Briefcase, 
  TrendingUp, BarChart3, Settings, Database, Shield, Activity,
  FileText, Newspaper, Calendar, Edit, Trash2, Eye, Plus, UserCog, DollarSign
} from 'lucide-react';
import EducationTab from '../components/super-admin/EducationTab';
import CorporateTab from '../components/super-admin/CorporateTab';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316'];

export default function SuperAdminDashboard({ user, logout, setUser }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // CMS States
  const [blogPosts, setBlogPosts] = useState([]);
  const [newsItems, setNewsItems] = useState([]);
  const [events, setEvents] = useState([]);
  const [cmsDialogOpen, setCmsDialogOpen] = useState(false);
  const [cmsType, setCmsType] = useState('blog'); // blog, news, event
  const [editingItem, setEditingItem] = useState(null);
  
  // Impersonation States
  const [allUsers, setAllUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  
  // Password Reset States
  const [passwordResetDialogOpen, setPasswordResetDialogOpen] = useState(false);
  const [selectedUserForReset, setSelectedUserForReset] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  
  // Edit User States
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '', role: '', institution_id: '', department: '', title: '' });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  
  // CMS Form States
  const [blogForm, setBlogForm] = useState({
    title: '',
    excerpt: '',
    content: '',
    author: user.name,
    category: 'Genel',
    featured: false,
    published: false
  });
  
  const [newsForm, setNewsForm] = useState({
    title: '',
    description: '',
    published: false
  });
  
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    event_date: '',
    event_time: '',
    location: '',
    attendees: '',
    registration_open: true,
    event_type: 'upcoming'
  });

  // Pricing States
  const [pricingType, setPricingType] = useState('school'); // school, expert, b2c
  const [pricingResult, setPricingResult] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  
  const [schoolPricingForm, setSchoolPricingForm] = useState({
    institution_name: '',
    city: '',
    school_type: 'K12',
    student_count: 500,
    has_guidance_department: false,
    primary_goals: [],
    budget_sensitivity: 'orta'
  });
  
  const [expertPricingForm, setExpertPricingForm] = useState({
    name: '',
    profession: 'psikolog',
    expected_clients_per_month: 10,
    expected_reports_per_month: 20,
    price_sensitivity: 'orta'
  });
  
  const [b2cPricingForm, setB2cPricingForm] = useState({
    adult_count: 1,
    child_count: 1,
    is_price_sensitive: false,
    wants_additional_content: false
  });

  // Education Management States (used by EducationTab component)
  const [institutions, setInstitutions] = useState([]);
  const [educationStats, setEducationStats] = useState({
    total_institutions: 0,
    total_teachers: 0,
    total_students: 0,
    total_classes: 0
  });
  
  // Corporate Management States (used by CorporateTab component)
  const [corporateStats, setCorporateStats] = useState({
    total_companies: 0,
    total_employees: 0,
    total_hr: 0,
    total_managers: 0
  });

  useEffect(() => {
    if (activeTab === 'cms') {
      fetchCMSContent();
    } else if (activeTab === 'impersonate') {
      fetchAllUsers();
    } else if (activeTab === 'education') {
      fetchEducationData();
    } else if (activeTab === 'corporate') {
      fetchCorporateData();
    }
  }, [activeTab]);

  const fetchCMSContent = async () => {
    setLoading(true);
    try {
      const [blogsRes, newsRes, eventsRes] = await Promise.all([
        api.get('/cms/blog'),
        api.get('/cms/news'),
        api.get('/cms/events')
      ]);
      setBlogPosts(blogsRes.data);
      setNewsItems(newsRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      console.error('Failed to fetch CMS content:', error);
      toast.error('İçerik yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    setLoading(true);
    try {
      // Fetch with pagination support (gets first 500 users)
      const response = await api.get('/users/all', {
        params: {
          skip: 0,
          limit: 500
        }
      });
      // Handle both old and new response format
      const users = response.data.users || response.data;
      setAllUsers(users);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Kullanıcılar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchEducationData = async () => {
    try {
      const [statsRes, institutionsRes] = await Promise.all([
        api.get('/education/stats'),
        api.get('/institutions/list')
      ]);
      setEducationStats(statsRes.data);
      setInstitutions(institutionsRes.data);
    } catch (error) {
      console.error('Failed to fetch education data:', error);
      toast.error('Eğitim verileri yüklenemedi');
    }
  };

  const fetchCorporateData = async () => {
    try {
      const response = await api.get('/corporate/stats');
      setCorporateStats(response.data);
    } catch (error) {
      console.error('Failed to fetch corporate data:', error);
      toast.error('Kurumsal veriler yüklenemedi');
    }
  };

  // CMS Handlers
  const handleCreateBlogPost = async () => {
    try {
      await api.post('/cms/blog', blogForm);
      toast.success('Blog yazısı oluşturuldu');
      setCmsDialogOpen(false);
      setBlogForm({
        title: '',
        excerpt: '',
        content: '',
        author: user.name,
        category: 'Genel',
        featured: false,
        published: false
      });
      fetchCMSContent();
    } catch (error) {
      console.error('Failed to create blog post:', error);
      toast.error('Blog yazısı oluşturulamadı');
    }
  };

  const handleCreateNews = async () => {
    try {
      await api.post('/cms/news', newsForm);
      toast.success('Haber oluşturuldu');
      setCmsDialogOpen(false);
      setNewsForm({
        title: '',
        description: '',
        published: false
      });
      fetchCMSContent();
    } catch (error) {
      console.error('Failed to create news:', error);
      toast.error('Haber oluşturulamadı');
    }
  };

  const handleCreateEvent = async () => {
    try {
      await api.post('/cms/events', eventForm);
      toast.success('Etkinlik oluşturuldu');
      setCmsDialogOpen(false);
      setEventForm({
        title: '',
        description: '',
        event_date: '',
        event_time: '',
        location: '',
        attendees: '',
        registration_open: true,
        event_type: 'upcoming'
      });
      fetchCMSContent();
    } catch (error) {
      console.error('Failed to create event:', error);
      toast.error('Etkinlik oluşturulamadı');
    }
  };

  const handleDeleteBlogPost = async (id) => {
    if (!confirm('Bu blog yazısını silmek istediğinize emin misiniz?')) return;
    try {
      await api.delete(`/cms/blog/${id}`);
      toast.success('Blog yazısı silindi');
      fetchCMSContent();
    } catch (error) {
      toast.error('Blog yazısı silinemedi');
    }
  };

  const handleDeleteNews = async (id) => {
    if (!confirm('Bu haberi silmek istediğinize emin misiniz?')) return;
    try {
      await api.delete(`/cms/news/${id}`);
      toast.success('Haber silindi');
      fetchCMSContent();
    } catch (error) {
      toast.error('Haber silinemedi');
    }
  };

  const handleDeleteEvent = async (id) => {
    if (!confirm('Bu etkinliği silmek istediğinize emin misiniz?')) return;
    try {
      await api.delete(`/cms/events/${id}`);
      toast.success('Etkinlik silindi');
      fetchCMSContent();
    } catch (error) {
      toast.error('Etkinlik silinemedi');
    }
  };

  // Impersonation Handlers
  const handleImpersonate = async (targetUserId) => {
    if (!confirm(`Bu kullanıcı olarak oturum açmak istediğinize emin misiniz?`)) return;
    
    try {
      const response = await api.post('/impersonate/', { target_user_id: targetUserId });
      
      // Store the new token and user
      const { access_token, user: targetUser, impersonation } = response.data;
      localStorage.setItem('access_token', access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser({ ...targetUser, impersonation });
      
      toast.success(`${targetUser.name} olarak giriş yapıldı`);
      
      // Redirect based on role
      setTimeout(() => {
        navigate('/');
      }, 1000);
    } catch (error) {
      console.error('Impersonation failed:', error);
      toast.error('Kullanıcı oturumu başlatılamadı');
    }
  };

  const handleStopImpersonation = async () => {
    try {
      const response = await api.post('/impersonate/stop');
      
      const { access_token, user: adminUser } = response.data;
      localStorage.setItem('access_token', access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(adminUser);
      
      toast.success('Süper admin hesabınıza döndünüz');
      navigate('/');
    } catch (error) {
      console.error('Stop impersonation failed:', error);
      toast.error('Oturum sonlandırılamadı');
    }
  };

  // Edit User Handlers
  const handleOpenEditUser = (u) => {
    setEditingUser(u);
    setEditForm({
      name: u.name || '',
      email: u.email || '',
      phone: u.phone || '',
      role: u.role || '',
      institution_id: u.institution_id || '',
      department: u.department || '',
      title: u.title || ''
    });
    setEditUserDialogOpen(true);
    // Ensure institutions are loaded for dropdown
    if (institutions.length === 0) {
      api.get('/institutions/list').then(res => setInstitutions(res.data || [])).catch(() => {});
    }
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;
    try {
      const response = await api.put(`/users/${editingUser.id}`, editForm);
      toast.success(response.data.message || 'Kullanıcı güncellendi');
      setEditUserDialogOpen(false);
      setEditingUser(null);
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Güncelleme başarısız');
    }
  };

  const handleDeleteUser = async () => {
    if (!deletingUser) return;
    try {
      const response = await api.delete(`/users/${deletingUser.id}`);
      toast.success(response.data.message || 'Kullanıcı silindi');
      setDeleteConfirmOpen(false);
      setDeletingUser(null);
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Silme başarısız');
    }
  };

  const filteredUsers = allUsers.filter(u => {
    const matchesSearch = u.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         u.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = selectedRole === 'all' || u.role === selectedRole;
    return matchesSearch && matchesRole;
  });

  // Pricing Handlers
  const handleCalculatePrice = async () => {
    setPricingLoading(true);
    setPricingResult(null);
    
    try {
      let endpoint = '';
      let payload = {};
      
      if (pricingType === 'school') {
        endpoint = '/pricing/school';
        payload = schoolPricingForm;
      } else if (pricingType === 'expert') {
        endpoint = '/pricing/expert';
        payload = expertPricingForm;
      } else if (pricingType === 'b2c') {
        endpoint = '/pricing/b2c';
        payload = b2cPricingForm;
      }
      
      const response = await api.post(endpoint, payload);
      // Backend returns the pricing data directly, not wrapped in 'quote'
      setPricingResult({ quote: response.data });
      toast.success('Fiyat teklifi başarıyla hesaplandı!');
    } catch (error) {
      console.error('Pricing calculation failed:', error);
      setPricingResult({ error: error.response?.data?.detail || 'Bir hata oluştu' });
      toast.error('Fiyat hesaplama başarısız oldu');
    } finally {
      setPricingLoading(false);
    }
  };

  // Admin Password Reset Handler
  const handleAdminPasswordReset = async () => {
    if (!selectedUserForReset || !newPassword) {
      toast.error('Lütfen yeni şifre girin');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Şifre en az 6 karakter olmalıdır');
      return;
    }

    try {
      await api.post('/auth/admin-reset-password', {
        user_id: selectedUserForReset.id,
        new_password: newPassword
      });
      toast.success(`${selectedUserForReset.name} kullanıcısının şifresi güncellendi`);
      setPasswordResetDialogOpen(false);
      setSelectedUserForReset(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Şifre güncellenemedi');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-emerald-600 rounded-xl flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800">Mizaç360</h1>
              <p className="text-sm text-slate-600">Süper Admin Paneli</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {user.impersonation?.active && (
              <Badge className="bg-orange-100 text-orange-700 animate-pulse">
                Taklit Modu: {user.impersonation.admin_name}
              </Badge>
            )}
            <Badge className="bg-purple-100 text-purple-700">Super Admin</Badge>
            <span className="text-sm text-slate-600">{user.name}</span>
            {user.impersonation?.active ? (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleStopImpersonation}
                className="bg-orange-50 border-orange-200"
              >
                Taklidi Durdur
              </Button>
            ) : null}
            <Button variant="outline" size="sm" onClick={logout}>
              <LogOut className="w-4 h-4 mr-2" />
              Çıkış
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-slate-800 mb-2">Platform Yönetimi</h2>
          <p className="text-slate-600">Eğitim ve kurumsal modüllerin merkezi yönetim paneli</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="overview">Genel Bakış</TabsTrigger>
            <TabsTrigger value="cms">İçerik Yönetimi</TabsTrigger>
            <TabsTrigger value="pricing">Fiyatlandırma</TabsTrigger>
            <TabsTrigger value="impersonate">Kullanıcı Yönetimi</TabsTrigger>
            <TabsTrigger value="education">Eğitim</TabsTrigger>
            <TabsTrigger value="corporate">Kurumsal</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <Card className="shadow-lg border-none">
              <CardHeader>
                <CardTitle>Platform İstatistikleri</CardTitle>
                <CardDescription>Sistemin genel durumu ve kullanım metrikleri</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8 text-slate-500">
                  <BarChart3 className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                  <p>İstatistikler yakında eklenecek</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CMS Tab */}
          <TabsContent value="cms" className="space-y-6">
            <Tabs defaultValue="blog" className="space-y-4">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="blog">Blog Yazıları</TabsTrigger>
                <TabsTrigger value="news">Haberler</TabsTrigger>
                <TabsTrigger value="events">Etkinlikler</TabsTrigger>
              </TabsList>

              {/* Blog Tab */}
              <TabsContent value="blog" className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-bold">Blog Yazıları</h3>
                  <Dialog open={cmsDialogOpen && cmsType === 'blog'} onOpenChange={(open) => {
                    setCmsDialogOpen(open);
                    if (open) setCmsType('blog');
                  }}>
                    <DialogTrigger asChild>
                      <Button className="bg-gradient-to-r from-blue-600 to-emerald-600">
                        <Plus className="w-4 h-4 mr-2" />
                        Yeni Blog Yazısı
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Yeni Blog Yazısı Oluştur</DialogTitle>
                        <DialogDescription>Blog sayfanızda görünecek yeni bir yazı ekleyin</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <Label>Başlık</Label>
                          <Input
                            value={blogForm.title}
                            onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })}
                            placeholder="Blog yazısı başlığı"
                          />
                        </div>
                        <div>
                          <Label>Özet</Label>
                          <Textarea
                            value={blogForm.excerpt}
                            onChange={(e) => setBlogForm({ ...blogForm, excerpt: e.target.value })}
                            placeholder="Kısa özet (liste görünümünde görünecek)"
                            rows={2}
                          />
                        </div>
                        <div>
                          <Label>İçerik</Label>
                          <Textarea
                            value={blogForm.content}
                            onChange={(e) => setBlogForm({ ...blogForm, content: e.target.value })}
                            placeholder="Blog yazısının tam içeriği"
                            rows={6}
                          />
                        </div>
                        <div>
                          <Label>Kategori</Label>
                          <Input
                            value={blogForm.category}
                            onChange={(e) => setBlogForm({ ...blogForm, category: e.target.value })}
                            placeholder="Örn: Psikoloji, Eğitim"
                          />
                        </div>
                        <div className="flex items-center gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={blogForm.featured}
                              onChange={(e) => setBlogForm({ ...blogForm, featured: e.target.checked })}
                            />
                            <span className="text-sm">Öne Çıkan</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={blogForm.published}
                              onChange={(e) => setBlogForm({ ...blogForm, published: e.target.checked })}
                            />
                            <span className="text-sm">Yayınla</span>
                          </label>
                        </div>
                        <Button 
                          onClick={handleCreateBlogPost}
                          className="w-full bg-gradient-to-r from-blue-600 to-emerald-600"
                        >
                          Oluştur
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>

                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                  </div>
                ) : blogPosts.length === 0 ? (
                  <Card>
                    <CardContent className="text-center py-12">
                      <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">Henüz blog yazısı bulunmuyor</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4">
                    {blogPosts.map((post) => (
                      <Card key={post.id} className="shadow-sm hover:shadow-md transition-shadow">
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <CardTitle className="text-lg">{post.title}</CardTitle>
                                {post.featured && <Badge className="bg-yellow-100 text-yellow-700">Öne Çıkan</Badge>}
                                {post.published ? (
                                  <Badge className="bg-green-100 text-green-700">Yayında</Badge>
                                ) : (
                                  <Badge className="bg-slate-100 text-slate-700">Taslak</Badge>
                                )}
                              </div>
                              <CardDescription>{post.excerpt}</CardDescription>
                              <div className="flex gap-4 mt-2 text-xs text-slate-500">
                                <span>Yazar: {post.author}</span>
                                <span>Kategori: {post.category}</span>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button variant="outline" size="sm" onClick={() => handleDeleteBlogPost(post.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* News Tab */}
              <TabsContent value="news" className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-bold">Haberler</h3>
                  <Dialog open={cmsDialogOpen && cmsType === 'news'} onOpenChange={(open) => {
                    setCmsDialogOpen(open);
                    if (open) setCmsType('news');
                  }}>
                    <DialogTrigger asChild>
                      <Button className="bg-gradient-to-r from-blue-600 to-emerald-600">
                        <Plus className="w-4 h-4 mr-2" />
                        Yeni Haber
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Yeni Haber Oluştur</DialogTitle>
                        <DialogDescription>Haber sayfanızda görünecek yeni bir haber ekleyin</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <Label>Başlık</Label>
                          <Input
                            value={newsForm.title}
                            onChange={(e) => setNewsForm({ ...newsForm, title: e.target.value })}
                            placeholder="Haber başlığı"
                          />
                        </div>
                        <div>
                          <Label>Açıklama</Label>
                          <Textarea
                            value={newsForm.description}
                            onChange={(e) => setNewsForm({ ...newsForm, description: e.target.value })}
                            placeholder="Haber içeriği"
                            rows={4}
                          />
                        </div>
                        <div>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={newsForm.published}
                              onChange={(e) => setNewsForm({ ...newsForm, published: e.target.checked })}
                            />
                            <span className="text-sm">Yayınla</span>
                          </label>
                        </div>
                        <Button 
                          onClick={handleCreateNews}
                          className="w-full bg-gradient-to-r from-blue-600 to-emerald-600"
                        >
                          Oluştur
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>

                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                  </div>
                ) : newsItems.length === 0 ? (
                  <Card>
                    <CardContent className="text-center py-12">
                      <Newspaper className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">Henüz haber bulunmuyor</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4">
                    {newsItems.map((news) => (
                      <Card key={news.id} className="shadow-sm hover:shadow-md transition-shadow">
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <CardTitle className="text-lg">{news.title}</CardTitle>
                                {news.published ? (
                                  <Badge className="bg-green-100 text-green-700">Yayında</Badge>
                                ) : (
                                  <Badge className="bg-slate-100 text-slate-700">Taslak</Badge>
                                )}
                              </div>
                              <CardDescription>{news.description}</CardDescription>
                            </div>
                            <div className="flex gap-2">
                              <Button variant="outline" size="sm" onClick={() => handleDeleteNews(news.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Events Tab */}
              <TabsContent value="events" className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-bold">Etkinlikler</h3>
                  <Dialog open={cmsDialogOpen && cmsType === 'event'} onOpenChange={(open) => {
                    setCmsDialogOpen(open);
                    if (open) setCmsType('event');
                  }}>
                    <DialogTrigger asChild>
                      <Button className="bg-gradient-to-r from-blue-600 to-emerald-600">
                        <Plus className="w-4 h-4 mr-2" />
                        Yeni Etkinlik
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Yeni Etkinlik Oluştur</DialogTitle>
                        <DialogDescription>Etkinlikler sayfanızda görünecek yeni bir etkinlik ekleyin</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <Label>Başlık</Label>
                          <Input
                            value={eventForm.title}
                            onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                            placeholder="Etkinlik başlığı"
                          />
                        </div>
                        <div>
                          <Label>Açıklama</Label>
                          <Textarea
                            value={eventForm.description}
                            onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                            placeholder="Etkinlik açıklaması"
                            rows={3}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Tarih</Label>
                            <Input
                              type="date"
                              value={eventForm.event_date}
                              onChange={(e) => setEventForm({ ...eventForm, event_date: e.target.value })}
                            />
                          </div>
                          <div>
                            <Label>Saat</Label>
                            <Input
                              type="time"
                              value={eventForm.event_time}
                              onChange={(e) => setEventForm({ ...eventForm, event_time: e.target.value })}
                            />
                          </div>
                        </div>
                        <div>
                          <Label>Konum</Label>
                          <Input
                            value={eventForm.location}
                            onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                            placeholder="Etkinlik konumu"
                          />
                        </div>
                        <div>
                          <Label>Katılımcı Sayısı</Label>
                          <Input
                            value={eventForm.attendees}
                            onChange={(e) => setEventForm({ ...eventForm, attendees: e.target.value })}
                            placeholder="Örn: 100 kişi"
                          />
                        </div>
                        <div>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={eventForm.registration_open}
                              onChange={(e) => setEventForm({ ...eventForm, registration_open: e.target.checked })}
                            />
                            <span className="text-sm">Kayıt Açık</span>
                          </label>
                        </div>
                        <Button 
                          onClick={handleCreateEvent}
                          className="w-full bg-gradient-to-r from-blue-600 to-emerald-600"
                        >
                          Oluştur
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>

                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                  </div>
                ) : events.length === 0 ? (
                  <Card>
                    <CardContent className="text-center py-12">
                      <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">Henüz etkinlik bulunmuyor</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4">
                    {events.map((event) => (
                      <Card key={event.id} className="shadow-sm hover:shadow-md transition-shadow">
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <CardTitle className="text-lg">{event.title}</CardTitle>
                                {event.registration_open ? (
                                  <Badge className="bg-green-100 text-green-700">Kayıt Açık</Badge>
                                ) : (
                                  <Badge className="bg-slate-100 text-slate-700">Kayıt Kapalı</Badge>
                                )}
                              </div>
                              <CardDescription>{event.description}</CardDescription>
                              <div className="flex gap-4 mt-2 text-xs text-slate-500">
                                <span>📅 {event.event_date} {event.event_time}</span>
                                <span>📍 {event.location}</span>
                                <span>👥 {event.attendees}</span>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button variant="outline" size="sm" onClick={() => handleDeleteEvent(event.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Impersonation Tab */}
          <TabsContent value="impersonate" className="space-y-6">
            <Card className="shadow-lg border-none">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserCog className="w-5 h-5 text-purple-600" />
                  Kullanıcı Yönetimi
                </CardTitle>
                <CardDescription>
                  Kullanıcı profillerini düzenleyin, rolleri değiştirin veya hesabı taklit edin
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Search and Filter */}
                <div className="flex gap-4 mb-6">
                  <div className="flex-1">
                    <Input
                      placeholder="Ad veya email ile ara..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      data-testid="user-search-input"
                    />
                  </div>
                  <select
                    className="border rounded-md px-3 py-2 bg-white"
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    data-testid="user-role-filter"
                  >
                    <option value="all">Tüm Roller</option>
                    <option value="student">Öğrenci</option>
                    <option value="parent">Veli</option>
                    <option value="teacher">Öğretmen</option>
                    <option value="institution_admin">Kurum Admin</option>
                    <option value="mudur">Müdür</option>
                    <option value="mudur_yardimcisi">Müdür Yrd.</option>
                    <option value="rehber_ogretmen">PDR</option>
                    <option value="psikolog">Psikolog</option>
                    <option value="employee">Çalışan</option>
                    <option value="manager">Yönetici</option>
                    <option value="hr">İK</option>
                    <option value="individual">Bireysel</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>

                <div className="text-sm text-slate-500 mb-4">
                  {filteredUsers.length} kullanıcı bulundu
                </div>

                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">Kullanıcı bulunamadı</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {filteredUsers.map((u) => (
                      <div
                        key={u.id}
                        className="flex justify-between items-center p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                        data-testid={`user-row-${u.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-800">{u.name}</p>
                          <p className="text-sm text-slate-600 truncate">{u.email}</p>
                          <div className="flex gap-2 mt-1 flex-wrap">
                            <Badge variant="outline" className={
                              u.role === 'super_admin' ? 'bg-red-50 text-red-700 border-red-200' :
                              u.role === 'institution_admin' ? 'bg-orange-50 text-orange-700 border-orange-200' :
                              u.role === 'mudur' ? 'bg-rose-50 text-rose-700 border-rose-200' :
                              u.role === 'rehber_ogretmen' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                              u.role === 'psikolog' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                              u.role === 'teacher' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                              u.role === 'student' ? 'bg-sky-50 text-sky-700 border-sky-200' :
                              'bg-slate-50 text-slate-700'
                            }>
                              {u.role === 'student' && 'Öğrenci'}
                              {u.role === 'parent' && 'Veli'}
                              {u.role === 'teacher' && 'Öğretmen'}
                              {u.role === 'institution_admin' && 'Kurum Admin'}
                              {u.role === 'mudur' && 'Müdür'}
                              {u.role === 'mudur_yardimcisi' && 'Müdür Yrd.'}
                              {u.role === 'rehber_ogretmen' && 'PDR'}
                              {u.role === 'psikolog' && 'Psikolog'}
                              {u.role === 'employee' && 'Çalışan'}
                              {u.role === 'manager' && 'Yönetici'}
                              {u.role === 'hr' && 'İK'}
                              {u.role === 'admin' && 'Admin'}
                              {u.role === 'individual' && 'Bireysel'}
                              {u.role === 'super_admin' && 'Super Admin'}
                            </Badge>
                            {u.institution_id && (
                              <Badge variant="outline" className="text-xs bg-slate-50">Kurum</Badge>
                            )}
                            {u.department && (
                              <Badge variant="outline" className="text-xs">{u.department}</Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4 flex-shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenEditUser(u)}
                            className="bg-blue-50 border-blue-200 hover:bg-blue-100"
                            data-testid={`edit-user-${u.id}`}
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Düzenle
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedUserForReset(u);
                              setNewPassword('');
                              setPasswordResetDialogOpen(true);
                            }}
                            className="bg-amber-50 border-amber-200 hover:bg-amber-100"
                          >
                            <Settings className="w-4 h-4 mr-1" />
                            Şifre
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleImpersonate(u.id)}
                            className="bg-purple-50 border-purple-200 hover:bg-purple-100"
                          >
                            <UserCog className="w-4 h-4 mr-1" />
                            Taklit
                          </Button>
                          {u.role !== 'super_admin' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => { setDeletingUser(u); setDeleteConfirmOpen(true); }}
                              className="bg-red-50 border-red-200 hover:bg-red-100 text-red-600"
                              data-testid={`delete-user-${u.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Password Reset Dialog */}
                <Dialog open={passwordResetDialogOpen} onOpenChange={setPasswordResetDialogOpen}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Şifre Sıfırla</DialogTitle>
                      <DialogDescription>
                        {selectedUserForReset?.name} ({selectedUserForReset?.email}) kullanıcısı için yeni şifre belirleyin
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label>Yeni Şifre</Label>
                        <Input
                          type="text"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Yeni şifre (min 6 karakter)"
                        />
                      </div>
                      <Button 
                        className="w-full bg-gradient-to-r from-amber-500 to-orange-600"
                        onClick={handleAdminPasswordReset}
                      >
                        Şifreyi Güncelle
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Edit User Dialog */}
                <Dialog open={editUserDialogOpen} onOpenChange={setEditUserDialogOpen}>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Kullanıcı Düzenle</DialogTitle>
                      <DialogDescription>
                        {editingUser?.name} - Profil bilgilerini ve rolü güncelleyin
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                      <div>
                        <Label>Ad Soyad *</Label>
                        <Input value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} data-testid="edit-user-name" />
                      </div>
                      <div>
                        <Label>Email *</Label>
                        <Input type="email" value={editForm.email} onChange={(e) => setEditForm({...editForm, email: e.target.value})} data-testid="edit-user-email" />
                      </div>
                      <div>
                        <Label>Telefon</Label>
                        <Input value={editForm.phone} onChange={(e) => setEditForm({...editForm, phone: e.target.value})} placeholder="05XX..." data-testid="edit-user-phone" />
                      </div>
                      <div>
                        <Label>Rol *</Label>
                        <select className="w-full border rounded-md px-3 py-2 mt-1" value={editForm.role}
                          onChange={(e) => setEditForm({...editForm, role: e.target.value})} data-testid="edit-user-role">
                          <option value="student">Öğrenci</option>
                          <option value="parent">Veli</option>
                          <option value="teacher">Öğretmen</option>
                          <option value="institution_admin">Kurum Yöneticisi</option>
                          <option value="mudur">Müdür</option>
                          <option value="mudur_yardimcisi">Müdür Yardımcısı</option>
                          <option value="rehber_ogretmen">Rehber Öğretmen (PDR)</option>
                          <option value="psikolog">Psikolog</option>
                          <option value="employee">Çalışan</option>
                          <option value="manager">Yönetici</option>
                          <option value="hr">İK Uzmanı</option>
                          <option value="individual">Bireysel</option>
                          <option value="admin">Admin</option>
                          <option value="super_admin">Super Admin</option>
                        </select>
                      </div>
                      {['teacher', 'student', 'institution_admin', 'mudur', 'mudur_yardimcisi', 'rehber_ogretmen', 'psikolog'].includes(editForm.role) && (
                        <div>
                          <Label>Kurum</Label>
                          <select className="w-full border rounded-md px-3 py-2 mt-1" value={editForm.institution_id}
                            onChange={(e) => setEditForm({...editForm, institution_id: e.target.value})} data-testid="edit-user-institution">
                            <option value="">Kurum seçin</option>
                            {institutions.map(inst => (
                              <option key={inst.id} value={inst.id}>{inst.name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      {['employee', 'manager', 'hr'].includes(editForm.role) && (
                        <>
                          <div>
                            <Label>Departman</Label>
                            <Input value={editForm.department} onChange={(e) => setEditForm({...editForm, department: e.target.value})} placeholder="Örn: İnsan Kaynakları" />
                          </div>
                          <div>
                            <Label>Ünvan</Label>
                            <Input value={editForm.title} onChange={(e) => setEditForm({...editForm, title: e.target.value})} placeholder="Örn: Müdür" />
                          </div>
                        </>
                      )}
                      <Button onClick={handleSaveUser} className="w-full bg-gradient-to-r from-blue-600 to-emerald-600" data-testid="save-user-btn">
                        Değişiklikleri Kaydet
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Delete Confirm Dialog */}
                <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Kullanıcıyı Sil</DialogTitle>
                      <DialogDescription>
                        <strong>{deletingUser?.name}</strong> ({deletingUser?.email}) kullanıcısını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="flex gap-3 pt-4">
                      <Button variant="outline" className="flex-1" onClick={() => setDeleteConfirmOpen(false)}>İptal</Button>
                      <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={handleDeleteUser} data-testid="confirm-delete-user">Evet, Sil</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pricing Tab */}
          <TabsContent value="pricing" className="space-y-6">
            <Card className="shadow-lg border-none">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  AI Fiyatlandırma Motoru
                </CardTitle>
                <CardDescription>
                  Farklı müşteri segmentleri için otomatik fiyat teklifi oluşturun
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Pricing Type Selector */}
                <div className="mb-6">
                  <Label className="mb-2 block">Müşteri Tipi</Label>
                  <div className="grid grid-cols-3 gap-4">
                    <Button
                      variant={pricingType === 'school' ? 'default' : 'outline'}
                      onClick={() => setPricingType('school')}
                      className="h-20"
                    >
                      <div className="text-center">
                        <Building2 className="w-6 h-6 mx-auto mb-1" />
                        <div>Okul</div>
                      </div>
                    </Button>
                    <Button
                      variant={pricingType === 'expert' ? 'default' : 'outline'}
                      onClick={() => setPricingType('expert')}
                      className="h-20"
                    >
                      <div className="text-center">
                        <UserCog className="w-6 h-6 mx-auto mb-1" />
                        <div>Uzman</div>
                      </div>
                    </Button>
                    <Button
                      variant={pricingType === 'b2c' ? 'default' : 'outline'}
                      onClick={() => setPricingType('b2c')}
                      className="h-20"
                    >
                      <div className="text-center">
                        <Users className="w-6 h-6 mx-auto mb-1" />
                        <div>Bireysel (B2C)</div>
                      </div>
                    </Button>
                  </div>
                </div>

                {/* School Pricing Form */}
                {pricingType === 'school' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Kurum Adı</Label>
                        <Input
                          value={schoolPricingForm.institution_name}
                          onChange={(e) => setSchoolPricingForm({ ...schoolPricingForm, institution_name: e.target.value })}
                          placeholder="Örn: ABC Koleji"
                        />
                      </div>
                      <div>
                        <Label>Şehir</Label>
                        <Input
                          value={schoolPricingForm.city}
                          onChange={(e) => setSchoolPricingForm({ ...schoolPricingForm, city: e.target.value })}
                          placeholder="Örn: İstanbul"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Okul Tipi</Label>
                        <select
                          className="w-full border rounded-md px-3 py-2"
                          value={schoolPricingForm.school_type}
                          onChange={(e) => setSchoolPricingForm({ ...schoolPricingForm, school_type: e.target.value })}
                        >
                          <option value="K12">K12</option>
                          <option value="anaokulu">Anaokulu</option>
                          <option value="lise">Lise</option>
                          <option value="kolej">Kolej</option>
                          <option value="kurs">Kurs/Dershane</option>
                        </select>
                      </div>
                      <div>
                        <Label>Öğrenci Sayısı</Label>
                        <Input
                          type="number"
                          value={schoolPricingForm.student_count}
                          onChange={(e) => setSchoolPricingForm({ ...schoolPricingForm, student_count: parseInt(e.target.value) })}
                        />
                      </div>
                    </div>

                    <div>
                      <Label>Bütçe Hassasiyeti</Label>
                      <select
                        className="w-full border rounded-md px-3 py-2"
                        value={schoolPricingForm.budget_sensitivity}
                        onChange={(e) => setSchoolPricingForm({ ...schoolPricingForm, budget_sensitivity: e.target.value })}
                      >
                        <option value="düşük">Düşük (Fiyat öncelikli değil)</option>
                        <option value="orta">Orta</option>
                        <option value="yüksek">Yüksek (Fiyat çok önemli)</option>
                      </select>
                    </div>

                    <div>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={schoolPricingForm.has_guidance_department}
                          onChange={(e) => setSchoolPricingForm({ ...schoolPricingForm, has_guidance_department: e.target.checked })}
                        />
                        <span className="text-sm">Rehberlik birimi var</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Expert Pricing Form */}
                {pricingType === 'expert' && (
                  <div className="space-y-4">
                    <div>
                      <Label>İsim</Label>
                      <Input
                        value={expertPricingForm.name}
                        onChange={(e) => setExpertPricingForm({ ...expertPricingForm, name: e.target.value })}
                        placeholder="Örn: Dr. Ayşe Yılmaz"
                      />
                    </div>
                    
                    <div>
                      <Label>Meslek</Label>
                      <select
                        className="w-full border rounded-md px-3 py-2"
                        value={expertPricingForm.profession}
                        onChange={(e) => setExpertPricingForm({ ...expertPricingForm, profession: e.target.value })}
                      >
                        <option value="psikolog">Psikolog</option>
                        <option value="PDR">PDR Uzmanı</option>
                        <option value="koç">Koç</option>
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Aylık Danışan Sayısı (Tahmini)</Label>
                        <Input
                          type="number"
                          value={expertPricingForm.expected_clients_per_month}
                          onChange={(e) => setExpertPricingForm({ ...expertPricingForm, expected_clients_per_month: parseInt(e.target.value) })}
                        />
                      </div>
                      <div>
                        <Label>Aylık Rapor Sayısı (Tahmini)</Label>
                        <Input
                          type="number"
                          value={expertPricingForm.expected_reports_per_month}
                          onChange={(e) => setExpertPricingForm({ ...expertPricingForm, expected_reports_per_month: parseInt(e.target.value) })}
                        />
                      </div>
                    </div>

                    <div>
                      <Label>Fiyat Hassasiyeti</Label>
                      <select
                        className="w-full border rounded-md px-3 py-2"
                        value={expertPricingForm.price_sensitivity}
                        onChange={(e) => setExpertPricingForm({ ...expertPricingForm, price_sensitivity: e.target.value })}
                      >
                        <option value="düşük">Düşük</option>
                        <option value="orta">Orta</option>
                        <option value="yüksek">Yüksek</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* B2C Pricing Form */}
                {pricingType === 'b2c' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Yetişkin Sayısı</Label>
                        <Input
                          type="number"
                          value={b2cPricingForm.adult_count}
                          onChange={(e) => setB2cPricingForm({ ...b2cPricingForm, adult_count: parseInt(e.target.value) })}
                        />
                      </div>
                      <div>
                        <Label>Çocuk Sayısı</Label>
                        <Input
                          type="number"
                          value={b2cPricingForm.child_count}
                          onChange={(e) => setB2cPricingForm({ ...b2cPricingForm, child_count: parseInt(e.target.value) })}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={b2cPricingForm.is_price_sensitive}
                          onChange={(e) => setB2cPricingForm({ ...b2cPricingForm, is_price_sensitive: e.target.checked })}
                        />
                        <span className="text-sm">Fiyata duyarlı</span>
                      </label>
                    </div>

                    <div>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={b2cPricingForm.wants_additional_content}
                          onChange={(e) => setB2cPricingForm({ ...b2cPricingForm, wants_additional_content: e.target.checked })}
                        />
                        <span className="text-sm">Ek içerik istiyor</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Calculate Button */}
                <div className="mt-6">
                  <Button
                    onClick={handleCalculatePrice}
                    disabled={pricingLoading}
                    className="w-full bg-gradient-to-r from-green-600 to-emerald-600"
                  >
                    {pricingLoading ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Hesaplanıyor...
                      </div>
                    ) : (
                      <>
                        <DollarSign className="w-4 h-4 mr-2" />
                        Fiyat Hesapla
                      </>
                    )}
                  </Button>
                </div>

                {/* Pricing Result */}
                {pricingResult && (
                  <div className="mt-6 p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-200">
                    <h3 className="text-lg font-bold text-green-800 mb-4 flex items-center gap-2">
                      <DollarSign className="w-5 h-5" />
                      Fiyat Teklifi Sonucu
                    </h3>
                    {pricingResult.quote ? (
                      <div className="space-y-4">
                        {/* Recommended Package */}
                        {pricingResult.quote.recommended_package && (
                          <div className="bg-white p-6 rounded-lg shadow-sm border-2 border-green-500">
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="text-xl font-bold text-green-700">
                                Önerilen Paket: {pricingResult.quote.recommended_package}
                              </h4>
                              <Badge className="bg-green-600 text-white">Önerilen</Badge>
                            </div>
                            {pricingResult.quote.packages && pricingResult.quote.packages[0] && (
                              <div className="space-y-3">
                                <p className="text-slate-600">{pricingResult.quote.packages[0].description}</p>
                                <div className="grid grid-cols-2 gap-4 mt-4">
                                  <div className="bg-slate-50 p-4 rounded">
                                    <p className="text-sm text-slate-500">Yıllık Sabit Ücret</p>
                                    <p className="text-2xl font-bold text-slate-800">
                                      ₺{pricingResult.quote.packages[0].annual_fixed_fee?.toLocaleString('tr-TR')}
                                    </p>
                                  </div>
                                  <div className="bg-slate-50 p-4 rounded">
                                    <p className="text-sm text-slate-500">Öğrenci Başı Ücret</p>
                                    <p className="text-2xl font-bold text-slate-800">
                                      ₺{pricingResult.quote.packages[0].per_student_fee?.toLocaleString('tr-TR')}
                                    </p>
                                  </div>
                                </div>
                                <div className="bg-green-50 p-4 rounded-lg mt-4">
                                  <p className="text-sm text-green-700 font-semibold mb-1">Toplam Tutar</p>
                                  <p className="text-3xl font-bold text-green-800">
                                    ₺{pricingResult.quote.packages[0].calculated_total?.toLocaleString('tr-TR')} / Yıl
                                  </p>
                                </div>
                                {pricingResult.quote.packages[0].services_included && (
                                  <div className="mt-4">
                                    <p className="font-semibold text-slate-700 mb-2">Dahil Olan Hizmetler:</p>
                                    <ul className="list-disc list-inside space-y-1 text-slate-600">
                                      {pricingResult.quote.packages[0].services_included.map((service, idx) => (
                                        <li key={idx}>{service}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Alternative Package */}
                        {pricingResult.quote.packages && pricingResult.quote.packages[1] && (
                          <div className="bg-white p-6 rounded-lg shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="text-lg font-bold text-slate-700">
                                Alternatif Paket: {pricingResult.quote.alternative_package}
                              </h4>
                              <Badge variant="outline">Alternatif</Badge>
                            </div>
                            <p className="text-slate-600 mb-3">{pricingResult.quote.packages[1].description}</p>
                            <div className="flex gap-4">
                              <div className="bg-slate-50 p-3 rounded flex-1">
                                <p className="text-xs text-slate-500">Yıllık Tutar</p>
                                <p className="text-xl font-bold text-slate-800">
                                  ₺{pricingResult.quote.packages[1].calculated_total?.toLocaleString('tr-TR')}
                                </p>
                              </div>
                              <div className="bg-slate-50 p-3 rounded flex-1">
                                <p className="text-xs text-slate-500">Öğrenci Başı</p>
                                <p className="text-xl font-bold text-slate-800">
                                  ₺{pricingResult.quote.packages[1].per_student_fee?.toLocaleString('tr-TR')}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : pricingResult.error ? (
                      <div className="bg-red-50 p-4 rounded-lg text-red-700 flex items-start gap-2">
                        <span className="text-lg">⚠️</span>
                        <div>
                          <p className="font-semibold mb-1">Hata Oluştu</p>
                          <p>{pricingResult.error}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-yellow-50 p-4 rounded-lg text-yellow-700">
                        Fiyat teklifi alınamadı. Lütfen tekrar deneyin.
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          {/* Education Tab */}
          <TabsContent value="education" className="space-y-6">
            <EducationTab 
              educationStats={educationStats}
              institutions={institutions}
              onRefresh={fetchEducationData}
              onImpersonate={handleImpersonate}
            />
          </TabsContent>

          {/* Corporate Tab */}
          <TabsContent value="corporate" className="space-y-6">
            <CorporateTab 
              corporateStats={corporateStats}
              onRefresh={fetchCorporateData}
            />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
